import { put, takeEvery, call, select } from 'redux-saga/effects';
import { matchPath } from 'react-router-dom';

import {
	sagaErrorDispatcher,
	sagaCallService,
	setLocalAuth,
	getLocalAuth,
	getMasterToken,
	getId,
} from 'Helpers';
import { userActions, messageActions } from 'Ducks';
import routes from 'Pages/router/constants/routes';
import authenticationSelectors from './selectors';
import authenticationServices from './services';
import authenticationActions from './actions';
import authenticationTypes from './types';
import { GLOBALS } from 'Constants';

function* emailLoginRequest(action) {
	try {
		let response = yield sagaCallService(
			authenticationServices.email_login,
			action,
			action.payload
		);
		yield call(handleLoginSuccess, {
			...response,
			navigate: action.payload.navigate,
		});
	} catch (e) {
		yield sagaErrorDispatcher(
			e,
			{ ...action, toastTime: GLOBALS.TOAST_TIMEOUT.UNSET },
			authenticationActions.loginFailure
		);
	}
}

function* otpLoginRequest(action) {
	try {
		let response = yield sagaCallService(
			authenticationServices.otp_login,
			action,
			action.payload
		);
		yield put(authenticationActions.loginByOtpSuccess(response.data));
	} catch (e) {
		yield sagaErrorDispatcher(
			e,
			{ ...action, toastTime: GLOBALS.TOAST_TIMEOUT.UNSET },
			authenticationActions.loginByOtpFailure
		);
	}
}

function* loginByIdRequest(action) {
	try {
		const id = getId();
		const payload = { ...action.payload, forceMasterToken: true };
		const apiFn =
			id === action.payload.business_id
				? authenticationServices.session_check
				: authenticationServices.login_by_id;

		const response = yield sagaCallService(apiFn, action, payload);
		const data = response?.data || {};
		const prevUser = yield select(authenticationSelectors.selectUser);
		const masterToken = getMasterToken();
		const uuid = data.uuid ?? null;
		const user = {
			...prevUser,
			email: data?.user?.email ?? prevUser?.email ?? null,
			name: data?.user?.name ?? prevUser?.name ?? null,
			role: data?.user?.role ?? prevUser?.role ?? null,
			business: {
				id: data?.business?.id ?? null,
				name: data?.business?.name ?? null,
			},
		};
		const auth = {
			businessId: id ?? null,
			masterToken: masterToken ?? null,
			activeBusinessToken: data?.token ?? null,
		};
		setLocalAuth({ auth, uuid });

		yield put(authenticationActions.loginByIdSuccess({ auth, user, uuid }));
		yield put(userActions.selectBusiness(data?.business?.id ?? null));
	} catch (error) {
		yield sagaErrorDispatcher(
			error,
			{ ...action, toastTime: GLOBALS.TOAST_TIMEOUT.UNSET },
			authenticationActions.loginByIdFailure
		);
	}
}

function* otpCheckRequest(action) {
	try {
		let response = yield sagaCallService(
			authenticationServices.otp_check,
			action,
			action.payload
		);
		response.isNewUser = action.payload?.is_fresh_merchant;
		yield call(handleLoginSuccess, {
			...response,
			navigate: action.payload.navigate,
		});
	} catch (e) {
		yield sagaErrorDispatcher(
			e,
			{ ...action, toastTime: GLOBALS.TOAST_TIMEOUT.UNSET },
			authenticationActions.loginFailure
		);
	}
}

function* handleLoginSuccess(response) {
	const data = response.data;
	const uuid = data?.uuid;
	const auth = {
		businessId: data?.business?.id,
		masterToken: data?.token ?? null,
		activeBusinessToken: data?.token ?? null,
	};
	const user =
		{
			id: data?.user?.id,
			uuid: data?.uuid,
			email: data?.user?.email,
			name: data?.user?.name,
			role: data?.user?.role,
			business: {
				id: data?.business?.id ?? null,
				name: data?.business?.name ?? null,
			},
			isSuperBusiness: data?.user?.is_super_business,
			renderer_token: data?.renderer_token,
		} ?? null;

	const businessList = [
		{
			id: data?.business?.id,
			name: data?.business?.name,
			workflow_state: data?.business?.workflow_state,
			type: 'business',
		},
		...(data?.sub_businesses ?? []).map((sb) => ({
			id: sb.id,
			name: sb.name,
			workflow_state: sb?.workflow_state,
			type: 'sub_business',
		})),
	];

	if (!auth.activeBusinessToken) return;

	const iranFun = [
		13335, 17107, 18591, 31343, 31344, 31345, 31346, 31347, 31349, 31350, 54307,
		54308,
	];
	const others = [9881, 7186];
	const total = [...iranFun, ...others];

	const businessId = data?.business?.id;

	const allBusinessIds = businessList.map((b) => b?.id).filter(Boolean);
	const shouldRedirect = allBusinessIds.some((id) => total.includes(id));
	if (shouldRedirect) {
		const loginUrl = data?.login_url || null;

		if (loginUrl) {
			window.location.href = loginUrl;
			return;
		}
	}

	if (
		businessId ||
		data?.user?.role === 'merchant' ||
		data?.user?.role === 'fresh_merchant'
	) {
		yield put(userActions.selectBusiness(businessId));
		yield* handlePwaLogin({
			auth,
			user,
			response,
			uuid,
			navigate: response.navigate,
			businessList,
		});
	} else {
		handlePanelLogin(auth, user, uuid);
	}
}

function* handlePwaLogin({
	auth,
	user,
	response,
	uuid,
	navigate,
	businessList,
}) {
	setLocalAuth({ auth, uuid });

	yield put(
		authenticationActions.loginSuccess({ auth, user, uuid, businessList })
	);

	if (user?.role === 'fresh_merchant') {
		yield call(navigate, routes.signupBusiness, { replace: true });
		return;
	}

	yield call(handleLoginRedirect, navigate);
}

function handlePanelLogin(auth, user, uuid) {
	localStorage.setItem('panel_auth', JSON.stringify({ auth, uuid }));
	const userWithRoleName = {
		...user,
		role_name: user.role,
	};
	localStorage.setItem('user', JSON.stringify({ user: userWithRoleName }));

	window.location.href = `/panel/app/dashboards/`;
}

const validGuestUrls = [
	routes.guest,
	routes.gtm,
	routes.login,
	routes.signup,
	routes.offlineVendors,
	routes.offlineContracts,
];

const excludedRedirectRoutes = [
	routes.login,
	routes.signup,
	routes.signupBusiness,
	routes.loginRedirect,
	routes.guest,
	routes.gtm,
	routes.offlineVendors,
	routes.offlineContracts,
	routes.forgetPasword,
];

function saveRedirectIfNeeded() {
	const modifiedString = window.location.href.split('pwa')[1];

	if (!excludedRedirectRoutes.includes(modifiedString)) {
		sessionStorage.setItem('redirect', modifiedString);
	}
}

function isGuestAllowed(pathname) {
	return validGuestUrls.some((pattern) => matchPath(pattern, pathname));
}

function redirectGuestUserIfNeeded(navigate, pathname) {
	if (!navigate) return;
	if (!isGuestAllowed(pathname)) {
		navigate(routes.login, { replace: true });
	}
}

export function* appInitRequest(action) {
	const { navigate, pathname } = action.payload || {};
	if (navigate && pathname && matchPath) {
		saveRedirectIfNeeded();
	}
	try {
		const state = yield select();
		const isAuthenticated = authenticationSelectors.selectAuthenticated(state);
		if (isAuthenticated) return;

		const { auth } = getLocalAuth() || {};
		if (!auth?.activeBusinessToken) {
			yield put(authenticationActions.logoutSuccess());
			redirectGuestUserIfNeeded(navigate, pathname);
			return;
		}

		let response = null;
		try {
			response = yield sagaCallService(
				authenticationServices.session_check,
				action,
				{
					...action.payload,
					forceMasterToken: true,
				}
			);
			yield call(handleLoginSuccess, {
				...response,
				navigate,
			});
		} catch (e) {
			sessionStorage.removeItem('redirect');
			Object.keys(localStorage).forEach((key) => {
				if (
					key !== 'app_version' &&
					key !== 'panel_auth' &&
					key !== 'user' &&
					!key.startsWith('price_list_images_')
				) {
					localStorage.removeItem(key);
				}
			});
			yield put(authenticationActions.logoutSuccess());
			redirectGuestUserIfNeeded(navigate, pathname);
			return;
		}

		if (navigate && [routes.login, routes.signup].includes(pathname)) {
			navigate(routes.dashboard, { replace: true });
		}
	} catch (e) {
		yield put(authenticationActions.logoutSuccess());

		const { navigate, pathname } = action.payload || {};
		redirectGuestUserIfNeeded(navigate, pathname);
	}
}

function* handleLoginRedirect(navigate) {
	const needToRedirect = sessionStorage.getItem('redirect');
	const currentHref = window.location.href;
	const isSignUpPage = currentHref.includes('/signup');

	if (
		needToRedirect &&
		!excludedRedirectRoutes.includes(needToRedirect) &&
		!isSignUpPage
	) {
		sessionStorage.removeItem('redirect');
		yield call(navigate, needToRedirect, { replace: true });
		return;
	}

	if (!isSignUpPage) {
		yield call(navigate, routes.dashboard, { replace: true });
	}
}

function* getSessionsRequest(action) {
	try {
		let response = yield sagaCallService(
			authenticationServices.get_sessions,
			action,
			action.payload
		);
		yield put(authenticationActions.getSessionsSuccess(response.data));
	} catch (e) {
		yield sagaErrorDispatcher(
			e,
			{ ...action, toastTime: GLOBALS.TOAST_TIMEOUT.UNSET },
			authenticationActions.getSessionsFailure
		);
	}
}

function* logoutRequest(action) {
	try {
		yield sagaCallService(
			authenticationServices.logout,
			action,
			action.payload
		);
		yield put(authenticationActions.logoutSuccess());
		yield put(userActions.userLogout());
		yield put(
			messageActions.pushSuccessToast({
				title: 'با موفقیت خارج شدید',
			})
		);
	} catch (e) {
		yield put(userActions.userLogout());
		yield put(authenticationActions.logoutSuccess());
		yield put(
			messageActions.pushSuccessToast({
				title: 'با موفقیت خارج شدید',
			})
		);
	}
}

const authenticationSagas = [
	takeEvery(authenticationTypes.LOGIN_BY_EMAIL.REQUEST, emailLoginRequest),
	takeEvery(authenticationTypes.LOGIN_BY_OTP.REQUEST, otpLoginRequest),
	takeEvery(authenticationTypes.LOGIN_BY_ID.REQUEST, loginByIdRequest),
	takeEvery(authenticationTypes.CHECK_OTP.REQUEST, otpCheckRequest),
	takeEvery(authenticationTypes.GET_SESSIONS.REQUEST, getSessionsRequest),
	takeEvery(authenticationTypes.LOGOUT.REQUEST, logoutRequest),
	takeEvery(authenticationTypes.APP_INIT.REQUEST, appInitRequest),
];

export default authenticationSagas;
